import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from './client'
import type { Operation, CreateOperationRequest, ListResult } from '@/types'

export function useOperations(tenantId: string | null) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['operations', tenantId],
    queryFn: () =>
      api.get<ListResult<Operation>>(`/api/v1/tenants/${tenantId}/operations`),
    enabled: !!tenantId,
  })
}

export function useOperation(tenantId: string | null, operationId: string | null) {
  const api = useApiClient()

  return useQuery({
    queryKey: ['operation', tenantId, operationId],
    queryFn: () =>
      api.get<Operation>(`/api/v1/tenants/${tenantId}/operations/${operationId}`),
    enabled: !!tenantId && !!operationId,
    refetchInterval: (query) => {
      const op = query.state.data
      if (op && (op.status === 'pending' || op.status === 'running')) return 5_000
      return false
    },
  })
}

export function useCreateOperation(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (req: CreateOperationRequest) =>
      api.post<Operation>(`/api/v1/tenants/${tenantId}/operations`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operations', tenantId] }),
  })
}

export function useCancelOperation(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (operationId: string) =>
      api.post<Operation>(`/api/v1/tenants/${tenantId}/operations/${operationId}/cancel`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operations', tenantId] }),
  })
}
